import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'

import { Bouton } from './Base'

/**
 * Modale accessible.
 *
 * Le focus entre dans la boîte à l'ouverture et revient à son point de départ
 * à la fermeture : sans cela, un utilisateur au clavier se retrouve renvoyé en
 * haut de page à chaque interaction, et un lecteur d'écran continue de lire le
 * contenu masqué derrière.
 */
export function Modale({
  ouverte,
  titre,
  description,
  children,
  onFermer,
}: {
  ouverte: boolean
  titre: string
  description?: string
  children?: ReactNode
  onFermer: () => void
}) {
  const boite = useRef<HTMLDivElement>(null)
  const declencheur = useRef<Element | null>(null)

  useEffect(() => {
    if (!ouverte) return
    declencheur.current = document.activeElement
    boite.current?.focus()

    function surTouche(evenement: KeyboardEvent) {
      if (evenement.key === 'Escape') onFermer()
    }
    document.addEventListener('keydown', surTouche)
    return () => {
      document.removeEventListener('keydown', surTouche)
      if (declencheur.current instanceof HTMLElement) declencheur.current.focus()
    }
  }, [ouverte, onFermer])

  if (!ouverte) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        ref={boite}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modale-titre"
        aria-describedby={description ? 'modale-description' : undefined}
        tabIndex={-1}
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl focus:outline-none"
      >
        <h2 id="modale-titre" className="text-lg font-semibold text-slate-900">
          {titre}
        </h2>
        {description ? (
          <p id="modale-description" className="mt-2 text-sm text-slate-600">
            {description}
          </p>
        ) : null}
        <div className="mt-4">{children}</div>
      </div>
    </div>
  )
}

/**
 * Confirmation d'une action destructrice.
 *
 * La modale **nomme l'objet** : « Annuler l'intervention chez Odette Berger du
 * 17/08 à 08:00 », pas « Êtes-vous sûr ? ». Une confirmation qui ne dit pas sur
 * quoi elle porte se clique sans être lue.
 */
export function Confirmation({
  ouverte,
  titre,
  description,
  libelleConfirmation = 'Confirmer',
  danger = false,
  enCours = false,
  onConfirmer,
  onAnnuler,
}: {
  ouverte: boolean
  titre: string
  description: string
  libelleConfirmation?: string
  danger?: boolean
  enCours?: boolean
  onConfirmer: () => void
  onAnnuler: () => void
}) {
  return (
    <Modale ouverte={ouverte} titre={titre} description={description} onFermer={onAnnuler}>
      <div className="flex justify-end gap-2">
        <Bouton variante="secondaire" onClick={onAnnuler} disabled={enCours}>
          Annuler
        </Bouton>
        <Bouton variante={danger ? 'danger' : 'principal'} onClick={onConfirmer} disabled={enCours}>
          {enCours ? 'En cours…' : libelleConfirmation}
        </Bouton>
      </div>
    </Modale>
  )
}
